import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PrimeReactProvider } from 'primereact/api';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import { ProgressBar } from 'primereact/progressbar';
import { Dropdown } from 'primereact/dropdown';
import { PLATFORM_META, getPlatformLogoSrc } from '../utils/streamerPreview';

const SCHOOL_NAME = 'UniStreaming';

const PLATFORM_OPTIONS = [
  { label: 'Twitch', value: 'twitch' },
  { label: 'Instagram', value: 'instagram' },
  { label: 'Kick', value: 'kick' },
  { label: 'YouTube', value: 'youtube' },
  { label: 'TikTok', value: 'tiktok' }
];

const emptySlot = () => ({ platform: null, handle: '' });

const StreamerSubmissionForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toastRef = useRef(null);

  const existingData = location.state?.submissionData || {};

  const [submission, setSubmission] = useState({
    name: existingData.name || '',
    email: existingData.email || '',
    subscribe_newsletter: existingData.subscribe_newsletter !== undefined ? existingData.subscribe_newsletter : true,
    bio: existingData.bio || ''
  });

  const [platformSlots, setPlatformSlots] = useState(
    existingData.platforms?.length
      ? [...existingData.platforms, emptySlot(), emptySlot(), emptySlot()].slice(0, 3)
      : [emptySlot(), emptySlot(), emptySlot()]
  );

  const [formProgress, setFormProgress] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filledPlatforms = platformSlots.filter((s) => s.platform && s.handle.trim());

  useEffect(() => {
    const baseFields = Object.values(submission).filter((val) => {
      if (typeof val === 'boolean') return true;
      return typeof val === 'string' && val.trim() !== '';
    }).length;
    const platformBonus = filledPlatforms.length > 0 ? 1 : 0;
    const totalFields = Object.keys(submission).length + 1;
    setFormProgress(((baseFields + platformBonus) / totalFields) * 100);
  }, [submission, filledPlatforms.length]);

  const getAvailablePlatforms = (slotIndex) => {
    const used = platformSlots
      .filter((_, i) => i !== slotIndex)
      .map((s) => s.platform)
      .filter(Boolean);
    return PLATFORM_OPTIONS.filter((o) => !used.includes(o.value));
  };

  const handleInputChange = (field, value) => {
    setSubmission((prev) => ({ ...prev, [field]: value }));
  };

  const updateSlot = (index, field, value) => {
    setPlatformSlots((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleNext = () => {
    if (!submission.name || !submission.bio) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Missing Information',
        detail: 'Please fill in your name and bio before continuing.',
        life: 3000
      });
      return;
    }

    if (!submission.email) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Missing Information',
        detail: 'Please fill in your email before continuing.',
        life: 3000
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(submission.email)) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Invalid Email',
        detail: 'Please enter a valid email address.',
        life: 3000
      });
      return;
    }

    if (filledPlatforms.length === 0) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Platform Required',
        detail: 'Add at least one platform with your handle.',
        life: 3000
      });
      return;
    }

    navigate('/streamer/media', {
      state: {
        submissionData: {
          ...submission,
          platforms: filledPlatforms
        },
        submissionId: location.state?.submissionId
      }
    });
  };

  const handleBackToLanding = () => {
    navigate('/');
  };

  const canContinue =
    submission.name &&
    submission.bio &&
    submission.email &&
    filledPlatforms.length > 0;

  const renderPlatformOption = (option) => {
    if (!option) return <span className="platform-dropdown-placeholder">Select platform</span>;
    return (
      <div className="platform-dropdown-option">
        <img src={getPlatformLogoSrc(option.value)} alt="" className="platform-dropdown-logo" />
        <span>{option.label}</span>
      </div>
    );
  };

  const renderPlatformValue = (option) => {
    if (!option) {
      return <span className="platform-dropdown-placeholder">Select platform</span>;
    }
    return (
      <div className="platform-dropdown-value">
        <img src={getPlatformLogoSrc(option.value)} alt="" className="platform-dropdown-logo" />
        <span>{option.label}</span>
      </div>
    );
  };

  return (
    <PrimeReactProvider>
      <div className="submission-page">
        <Toast ref={toastRef} />

        <header className="form-header">
          <div className="header-content">
            <Button
              icon="pi pi-arrow-left"
              label="Back"
              className="back-button"
              onClick={handleBackToLanding}
            />
            <div className="header-title">
              <p>{SCHOOL_NAME}</p>
            </div>
          </div>
        </header>

        <div className="progress-section">
          <div className="container">
            <div className="progress-info">
              <span>Step 1 of 3: Profile Info</span>
              <span>{Math.round(formProgress)}% Complete</span>
            </div>
            <ProgressBar
              value={formProgress}
              className="form-progress"
              showValue={false}
            />
          </div>
        </div>

        <main className="form-main">
          <div className="container">
            <Card className="form-card">
              <div className="form-header-info">
                <h2>Tell Us About Yourself</h2>
              </div>

              <div className="submission-form">
                <div className="form-grid">
                  <div className="form-field">
                    <label htmlFor="name">Name *</label>
                    <InputText
                      id="name"
                      value={submission.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Your name"
                      required
                      className="w-full"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="email">Email *</label>
                    <InputText
                      id="email"
                      type="email"
                      value={submission.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="email@example.com"
                      className="w-full"
                    />
                  </div>

                  <div className="form-field" style={{ justifyContent: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.0rem' }}>
                      <input
                        type="checkbox"
                        id="subscribe_newsletter"
                        name="subscribe_newsletter"
                        checked={submission.subscribe_newsletter}
                        onChange={(e) => handleInputChange('subscribe_newsletter', e.target.checked)}
                        style={{
                          width: '17px',
                          height: '17px',
                          cursor: 'pointer',
                          accentColor: '#6E8CFB',
                        }}
                      />
                      <label
                        htmlFor="subscribe_newsletter"
                        style={{ cursor: 'pointer', marginBottom: 0, fontWeight: 'normal' }}
                      >
                        Subscribe to newsletter
                      </label>
                    </div>
                  </div>

                  <div className="form-field full-width">
                    <label htmlFor="bio">Bio *</label>
                    <InputTextarea
                      id="bio"
                      value={submission.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      placeholder="e.g. My name is Gio, I'm from Philly and mostly stream Valorant. I go live Tues and Thurs evenings etc..."
                      rows={4}
                      className="w-full"
                      required
                    />
                    <small className="field-help">This will be featured in your Instagram post!</small>
                  </div>

                  <div className="form-field full-width">
                    <label>Your Platforms (1–3) *</label>
                    <small className="field-help" style={{ display: 'block', marginBottom: '0.75rem' }}>
                      Select up to 3 platforms. Your handles will appear on your cover photo.
                    </small>
                    <div className="platform-slots">
                      {platformSlots.map((slot, index) => (
                        <div
                          key={index}
                          className={`platform-slot-card ${slot.platform ? 'has-platform' : ''}`}
                        >
                          <div className="platform-slot-header">
                            <span className="platform-slot-number">Platform {index + 1}</span>
                            {index === 0 && <span className="platform-slot-required">Required</span>}
                          </div>
                          <div className="platform-slot-row">
                            <Dropdown
                              value={slot.platform}
                              options={getAvailablePlatforms(index)}
                              onChange={(e) => updateSlot(index, 'platform', e.value)}
                              placeholder={`Choose platform${index === 0 ? ' *' : ''}`}
                              className="platform-dropdown w-full"
                              panelClassName="platform-dropdown-panel"
                              itemTemplate={renderPlatformOption}
                              valueTemplate={() =>
                                renderPlatformValue(
                                  PLATFORM_OPTIONS.find((o) => o.value === slot.platform)
                                )
                              }
                            />
                            <div className="platform-handle-wrap">
                              <span className="platform-handle-prefix">@</span>
                              <InputText
                                value={slot.handle.replace(/^@/, '')}
                                onChange={(e) => updateSlot(index, 'handle', e.target.value)}
                                placeholder="username"
                                disabled={!slot.platform}
                                className="platform-handle-input w-full"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck="false"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <Divider />

                <div className="form-actions">
                  <Button
                    label="Next: Cover & Clips"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    onClick={handleNext}
                    className="next-button"
                    disabled={!canContinue}
                  />
                  <p className="form-note">
                    Complete your profile information to continue to media upload.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </main>
      </div>
    </PrimeReactProvider>
  );
};

export default StreamerSubmissionForm;
